import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { FileSpreadsheet, FileText, Loader2, Search } from "lucide-react";

const LABEL = "text-xs font-medium uppercase tracking-wider text-slate-500";
const DEFAULT_FIELDS = ["name", "dni", "position", "phone"];

export default function ExportDialog({ open, employees, onClose }) {
  const [fields, setFields] = useState([]);
  const [chosen, setChosen] = useState(DEFAULT_FIELDS);
  const [ids, setIds] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");

  useEffect(() => { if (open) api.get("/export-fields").then((r) => setFields(r.data)).catch(() => {}); }, [open]);

  const active = useMemo(() => employees.filter((e) => e.active !== false), [employees]);
  const shown = useMemo(
    () => active.filter((e) => e.name.toLowerCase().includes(q.toLowerCase())),
    [active, q]
  );
  const allShown = shown.length > 0 && shown.every((e) => ids.includes(e.id));

  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const download = async (kind) => {
    if (!ids.length) return toast.error("Selecciona al menos un trabajador");
    if (!chosen.length) return toast.error("Selecciona al menos un campo");
    setBusy(kind);
    try {
      const res = await api.post(`/employees/export-${kind}`, { employee_ids: ids, fields: chosen }, { responseType: "blob" });
      const m = (res.headers["content-disposition"] || "").match(/filename="?([^"]+)"?/);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = m ? m[1] : `listado.${kind === "xlsx" ? "xlsx" : "pdf"}`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Listado generado");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl w-[calc(100vw-1rem)] sm:w-full max-h-[92vh] overflow-hidden flex flex-col p-5 sm:p-6" data-testid="export-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Exportar listado de personal</DialogTitle>
          <DialogDescription>Elige los trabajadores y los campos que quieres incluir. Solo administradores.</DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 overflow-y-auto py-2 md:grid-cols-2">
          <section>
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
              <p className={LABEL}>Trabajadores ({ids.length})</p>
              <button className="shrink-0 text-xs font-medium text-accent hover:underline" data-testid="export-select-all"
                onClick={() => setIds(allShown ? ids.filter((i) => !shown.some((e) => e.id === i)) : [...new Set([...ids, ...shown.map((e) => e.id)])])}>
                {allShown ? "Quitar todos" : "Seleccionar todos"}
              </button>
            </div>
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input className="pl-9" placeholder="Buscar…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="export-search" />
            </div>
            <ul className="mt-3 max-h-64 divide-y overflow-y-auto rounded-md border">
              {shown.map((e) => (
                <li key={e.id}>
                  <label className="flex cursor-pointer items-center gap-3 p-2.5 text-sm transition-colors hover:bg-slate-50">
                    <input type="checkbox" className="h-4 w-4 accent-primary" checked={ids.includes(e.id)}
                      onChange={() => toggle(ids, setIds, e.id)} data-testid={`export-employee-${e.id}`} />
                    <span className="min-w-0 flex-1 truncate">{e.name}</span>
                    <span className="hidden max-w-[45%] shrink-0 truncate text-xs text-muted-foreground sm:inline">{e.position}</span>
                  </label>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <p className={LABEL}>Campos a incluir ({chosen.length})</p>
            <ul className="mt-2 max-h-[21.5rem] divide-y overflow-y-auto rounded-md border">
              {fields.map((f) => (
                <li key={f.key}>
                  <label className="flex cursor-pointer items-center gap-3 p-2.5 text-sm transition-colors hover:bg-slate-50">
                    <input type="checkbox" className="h-4 w-4 accent-primary" checked={chosen.includes(f.key)}
                      onChange={() => toggle(chosen, setChosen, f.key)} data-testid={`export-field-${f.key}`} />
                    {f.label}
                  </label>
                </li>
              ))}
            </ul>
          </section>
        </div>

        <DialogFooter className="flex-row justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={!!busy}>Cerrar</Button>
          <Button variant="outline" onClick={() => download("pdf")} disabled={!!busy} data-testid="export-pdf-button">
            {busy === "pdf" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />} PDF
          </Button>
          <Button onClick={() => download("xlsx")} disabled={!!busy} data-testid="export-xlsx-button">
            {busy === "xlsx" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />} Excel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
